import { closeLightbox } from '../../../libs/lightbox/utils/dispatch-lightbox-events.js';
import { div, domEl } from '../../../scripts/dom-builder.js';
import { mediaQueryLists } from '../../../scripts/utils/breakpoints-utils.js';
import parseJSONSafe from '../../../scripts/utils/parse-json-safe.js';
import getI18next from '../../../scripts/utils/translations/get-translations.js';
import { fetchContentFragmentByPath } from './utils/common.js';
import { processFragmentData } from './utils/data-transformer.js';

const tourInstances = new WeakMap();

export function getTourInstance(block) {
  return tourInstances.get(block);
}

/**
 * Product Tour component with streamlined implementation
 * Eliminates code duplication and feature flags
 */

/**
 * Creates and returns the appropriate tour implementation
 * @param {Object} i18next - The i18next instance for translations
 * @returns {Promise<Object>} The platform-specific tour instance
 */
async function createTourInstance(i18next) {
  if (mediaQueryLists.L.matches) {
    const { default: DesktopProductTour } = await import(
      './utils/desktop-tour.js'
    );
    return new DesktopProductTour(i18next);
  }

  const { default: MobileProductTour } = await import('./utils/mobile-tour.js');
  return new MobileProductTour(i18next);
}

/**
 * Renders the product tour content based on viewport
 * @param {Element} block - The block element to render into
 * @param {Object} fragmentItem - The content fragment data
 * @param {Object} i18next - The i18next instance for translations
 */
async function renderTourContent(block, fragmentItem, i18next) {
  const fragment = parseJSONSafe(fragmentItem.fields.content, {});
  block.textContent = '';

  // Create container structure
  const container = domEl('section', { class: 'gt_container' });

  // Create appropriate tour instance
  const tourInstance = await createTourInstance(i18next);

  // Store tour instance on block for cleanup access
  tourInstances.set(block, tourInstance);

  if (mediaQueryLists.L.matches) {
    renderDesktopTour(container, fragment, tourInstance, block);
  } else {
    renderMobileTour(container, fragment, tourInstance, block);

    // close lightbox for mobile instance as it not includes enlarge button
    if (document.querySelector('.product-tour-lightbox')) {
      closeLightbox();
    }
  }

  // Append container and set initial state
  block.appendChild(container);
  block.setAttribute('data-current-step', '1');
}

/**
 * Render desktop tour layout
 * @param {Element} container - Container element
 * @param {Object} fragment - Fragment data
 * @param {Object} tourInstance - Tour instance
 * @param {Element} block - Block element
 */
function renderDesktopTour(container, fragment, tourInstance, block) {
  // Desktop section structure
  const desktopSection = div(
    { class: 'row gt_desktop' },
    div({ class: 'col-3' }, div({ class: 'gt_nav-wrapper', role: 'list' })),
    div({ class: 'col-9', 'data-type': 'image' }),
  );

  const navWrapper = desktopSection.querySelector('.gt_nav-wrapper');
  const contentColumn = desktopSection.querySelector('.col-9');

  // Add overview section
  navWrapper.append(tourInstance.decorateOverviewSection(fragment));

  // Add section items
  fragment.section.forEach((sectionItem, index) => {
    const isLastSection = index === fragment.section.length - 1;
    const sectionEl = tourInstance.decorateSection(
      sectionItem,
      index,
      1,
      isLastSection,
    );
    navWrapper.append(sectionEl);
  });

  // Create desktop content
  tourInstance.createDesktopContent(contentColumn, fragment);
  container.append(desktopSection);

  // Attach event listeners
  attachTourListeners(tourInstance, container, block, fragment);
}

/**
 * Render mobile tour layout
 * @param {Element} container - Container element
 * @param {Object} fragment - Fragment data
 * @param {Object} tourInstance - Tour instance
 * @param {Element} block - Block element
 */
function renderMobileTour(container, fragment, tourInstance, block) {
  // Mobile section structure
  const mobileSection = div(
    { class: 'row gt_mobile' },
    div({ class: 'col-12 gt_mobile-main', role: 'list' }),
  );

  const mainColumn = mobileSection.querySelector('.col-12');

  // Add overview section
  mainColumn.append(tourInstance.decorateOverviewSection(fragment));

  // Add section items
  fragment.section.forEach((sectionItem, index) => {
    const isLastSection = index === fragment.section.length - 1;
    const sectionEl = tourInstance.decorateSection(
      sectionItem,
      index,
      1,
      isLastSection,
    );
    mainColumn.append(sectionEl);
  });

  // Add extra section
  mobileSection.append(tourInstance.createExtraSection(fragment, false));
  container.append(mobileSection);

  // Attach event listeners
  attachTourListeners(tourInstance, container, block, fragment);
}

/**
 * Attach all necessary event listeners
 * @param {Object} tourInstance - Tour instance
 * @param {Element} container - Container element
 * @param {Element} block - Block element
 * @param {Object} fragment - Fragment data
 */
function attachTourListeners(tourInstance, container, block, fragment) {
  // Navigation listeners
  tourInstance.addNavListeners(container, fragment);

  // Start button listeners
  tourInstance.attachStartButtonListeners(block, fragment);

  // Navigation button listeners
  tourInstance.attachNavigationListeners(block, fragment);
}

/**
 * Handle viewport changes with proper cleanup and re-rendering
 * @param {Element} block - Block element
 * @param {Object} fragmentData - Fragment data
 * @param {Object} i18next - The i18next instance for translations
 */
function setupViewportChangeHandler(block, fragmentData, i18next) {
  // Debounce function to prevent rapid re-renders
  const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn(...args), delay);
    };
  };

  // Handle viewport changes
  const handleViewportChange = debounce(async () => {
    const tourInstance = getTourInstance(block);

    // Clean up existing listeners using the tour instance's cleanup method
    tourInstance?.cleanupEventListeners?.(block);

    // Re-render the component
    await renderTourContent(block, fragmentData.items[0], i18next);
  }, 250); // 250ms debounce delay

  // Add viewport change listeners
  const breakpointListener = () => {
    handleViewportChange();
  };

  // Listen for relevant breakpoint changes
  Object.values(mediaQueryLists).forEach((mql) => {
    mql.addEventListener('change', breakpointListener);
  });

  // Clean up event listeners when the block is removed
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.removedNodes.forEach((node) => {
        if (node !== block) {
          return;
        }

        const tourInstance = getTourInstance(block);
        // Clean up tour event listeners
        tourInstance?.cleanupEventListeners?.(block);

        // Remove all breakpoint listeners
        Object.values(mediaQueryLists).forEach((mql) => {
          mql.removeEventListener('change', breakpointListener);
        });
        observer.disconnect();
      });
    });
  });

  observer.observe(block.parentNode, { childList: true });
}

export default async function decorate(block) {
  const link = block.querySelector('a');
  const path = link ? link.textContent.trim() : block.textContent.trim();

  if (!path) {
    return;
  }

  try {
    // Load data concurrently
    const [i18next, fragmentData] = await Promise.all([
      getI18next(),
      fetchContentFragmentByPath(path),
    ]);

    if (fragmentData?.items?.length) {
      const { compatibleFragmentItem, transformedFragmentData } =
        processFragmentData(fragmentData);

      await renderTourContent(block, compatibleFragmentItem, i18next);
      setupViewportChangeHandler(block, transformedFragmentData, i18next);
    } else {
      block.innerHTML =
        '<h4>No content available for the selected product tour.</h4>';
    }
  } catch (error) {
    // Show simple error message
    block.innerHTML = '<h4>Unable to load product tour content</h4>';
  }
}
